import { useEffect, useState, useMemo } from "react";
import { Heading, Subheading } from "../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { db } from "../../firebase";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { Call } from "../../models/Call";
import moment from "moment";
import { Text } from "../../design/text";
import { Badge } from "../../design/badge";
import { ChartBarIcon, ClockIcon, ServerIcon } from "@heroicons/react/20/solid";

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<Call[]>([]);

  useEffect(() => {
    const docRef = query(
      collection(db, "calls"),
      where("userId", "==", user?.uid),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        const items = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Call,
        );
        setItems(items);
      },
      (err) => {
        console.error(err);
      },
    );
    return () => {
      unsubscribe();
    };
  }, []);

  const stats = useMemo(() => {
    const total = items.length;
    const today = items.filter((item) =>
      moment(item.createdAt?.toDate()).isSame(moment(), 'day')
    ).length;

    const endpointCounts = items.reduce((acc, item) => {
      acc[item.endpoint] = (acc[item.endpoint] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topEndpoint = Object.entries(endpointCounts)
      .sort(([, a], [, b]) => b - a)[0];

    return { total, today, topEndpoint: topEndpoint ? topEndpoint[0] : "N/A" };
  }, [items]);

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <Heading>API Usage</Heading>
          <Text className="mt-2 text-gray-600 dark:text-gray-400">
            Review your API usage and monitor activity
          </Text>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3">
          <div className="p-6 bg-white rounded-lg shadow-md dark:bg-zinc-900">
            <div className="flex items-center gap-4 mb-2">
              <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <Text className="text-sm text-gray-600 dark:text-gray-400">Total Calls</Text>
                <Text className="text-3xl font-bold">{stats.total.toLocaleString()}</Text>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-lg shadow-md dark:bg-zinc-900">
            <div className="flex items-center gap-4 mb-2">
              <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                <ClockIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <Text className="text-sm text-gray-600 dark:text-gray-400">Today</Text>
                <Text className="text-3xl font-bold">{stats.today.toLocaleString()}</Text>
              </div>
            </div>
          </div>

          <div className="p-6 bg-white rounded-lg shadow-md dark:bg-zinc-900">
            <div className="flex items-center gap-4 mb-2">
              <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                <ServerIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <Text className="text-sm text-gray-600 dark:text-gray-400">Top Endpoint</Text>
                <Text className="text-lg font-bold truncate">{stats.topEndpoint}</Text>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Table */}
        <div className="bg-white rounded-lg shadow-md dark:bg-zinc-900">
          <div className="p-6">
            <Subheading className="mb-4">Recent Activity</Subheading>
            {items.length === 0 ? (
              <div className="py-12 text-center">
                <ServerIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <Text className="text-gray-600 dark:text-gray-400">No API calls yet</Text>
                <Text className="mt-1 text-sm text-gray-500">
                  Start making requests to see activity here
                </Text>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeader>Time</TableHeader>
                      <TableHeader>Endpoint</TableHeader>
                      <TableHeader>Status</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.slice(0, 50).map((result: any) => {
                      const isToday = moment(result.createdAt?.toDate()).isSame(moment(), 'day');
                      return (
                        <TableRow key={result.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <ClockIcon className="w-4 h-4 text-gray-400" />
                              <Text className="text-sm">
                                {moment(result.createdAt?.toDate()).format(
                                  "MMM D, YYYY h:mm A",
                                )}
                              </Text>
                              {isToday && (
                                <Badge color="blue" className="text-xs">Today</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="px-2 py-1 text-xs bg-gray-100 dark:bg-zinc-800 rounded">
                              {result.endpoint}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge color="green">Success</Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {items.length > 50 && (
                  <div className="mt-4 text-center">
                    <Text className="text-sm text-gray-500">
                      Showing 50 of {items.length.toLocaleString()} calls
                    </Text>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default DashboardPage;
